// PWA Prompts using Alpine.js
class PWAPrompts {
    constructor() {
        this.showPrompt = false;
        this.resolve = null;
        this.container = null;
        this.initialized = false;
        this.initializationPromise = null;
        this.initializationAttempts = 0;
        this.maxInitializationAttempts = 10;
        
        // Initialize on first load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initialize());
        } else {
            this.initialize();
        }

        // Re-initialize on Livewire navigation
        document.addEventListener('livewire:navigated', () => {
            console.log('Livewire navigation detected, reinitializing PWA prompts');
            this.initialize();
        });
    }

    async initialize() {
        // If already initializing, return the existing promise
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        const attemptInitialization = async () => {
            try {
                while (!window.Alpine) {
                    console.log('Waiting for Alpine.js to be available...');
                    if (++this.initializationAttempts >= this.maxInitializationAttempts) {
                        throw new Error(
                            'Failed to initialize PWAPrompts: Alpine.js not available after maximum attempts',
                        );
                    }
                    await new Promise(r => setTimeout(r, 100));
                }

                // Remove existing container if it exists
                if (this.container) {
                    this.container.remove();
                }

                // Create and append the prompts container
                this.container = document.createElement('div');
                this.container.innerHTML = `
                    <div x-data="{ 
                        showPrompt: false,
                        resolve: null,
                        show() {
                            this.showPrompt = true;
                            return new Promise((resolve) => {
                                this.resolve = resolve;
                            });
                        },
                        handleAction() {
                            this.showPrompt = false;
                            if (this.resolve) {
                                this.resolve(true);
                            }
                        },
                        handleCancel() {
                            this.showPrompt = false;
                            if (this.resolve) {
                                this.resolve(false);
                            }
                        }
                    }" 
                    x-show="showPrompt" 
                    class="fixed bottom-4 right-4 z-50"
                    x-transition:enter="transition ease-out duration-300"
                    x-transition:enter-start="opacity-0 transform translate-y-2"
                    x-transition:enter-end="opacity-100 transform translate-y-0"
                    x-transition:leave="transition ease-in duration-200"
                    x-transition:leave-start="opacity-100 transform translate-y-0"
                    x-transition:leave-end="opacity-0 transform translate-y-2">
                        <div class="bg-white/50 dark:bg-black/50 dark:card-green rounded-lg shadow-lg p-4 max-w-sm">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <svg class="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm font-medium text-black dark:text-white">
                                        A New version is available!
                                    </h3>
                                    <p class="mt-1 text-sm text-black dark:text-white">
                                        Click refresh to update to the latest version.
                                    </p>
                                </div>
                            </div>
                            <div class="mt-4 flex justify-end space-x-3">
                                <button @click="handleCancel" 
                                        class="px-3 py-2 text-sm font-medium text-black dark:text-white hover:text-gray-500 dark:hover:text-gray-400">
                                    Cancel
                                </button>
                                <button @click="handleAction" 
                                        class="px-3 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-md glow-green">
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                `;
                document.body.appendChild(this.container);

                // Initialize Alpine.js
                window.Alpine.initTree(this.container);

                this.initialized = true;
                console.log('PWAPrompts initialized successfully');
            } catch (error) {
                console.error('Error initializing PWAPrompts:', error);
                this.initializationPromise = null; // Clear the promise on error
                throw error; // Re-throw to maintain error propagation
            }
        };

        this.initializationPromise = attemptInitialization();
        return this.initializationPromise;
    }

    async show() {
        console.log('PWA Update available!');
        
        // Ensure Alpine.js is initialized
        if (!window.Alpine) {
            console.error('Alpine.js is not loaded');
            return false;
        }

        try {
            // Wait for initialization if needed
            if (!this.initialized) {
                console.log('Waiting for PWAPrompts initialization...');
                await this.initialize();
            }

            // Get the Alpine component instance
            const component = window.Alpine.$data(this.container.querySelector('[x-data]'));
            if (!component) {
                console.error('Could not find PWA prompts Alpine component instance');
                return false;
            }

            return await component.show();
        } catch (error) {
            console.error('Error showing PWA prompt:', error);
            return false;
        }
    }
}

// Create and export a singleton instance
const pwaPrompts = new PWAPrompts();
export default pwaPrompts; 